/**
 * @fileoverview Hook to manage new user onboarding state
 * 
 * Determines if a user needs to complete onboarding (set country, gender)
 * including display_name and handle for new users.
 * and provides the completion handler.
 * 
 * IMPORTANT: Only triggers for NEW users (accounts created in the last 5 minutes).
 * Existing users who haven't filled in new fields will NOT be blocked.
 */

import { useCallback, useMemo } from 'react';
import { useAuth } from './useAuth';
import { useProfile } from './useProfile';

// Time window in milliseconds for considering an account "new" (5 minutes)
const NEW_ACCOUNT_WINDOW_MS = 5 * 60 * 1000;

export function useNewUserOnboarding() {
  const { user, loading: authLoading } = useAuth();
  const { profile, loading: profileLoading, updateProfile } = useProfile();

  // Only show onboarding modal for genuinely new users (created in last 5 minutes)
  // AND who haven't completed the required fields
  const needsOnboarding = useMemo(() => {
    if (authLoading || profileLoading) return false;
    if (!user || !profile) return false;
    
    // Check if all required fields are filled
    const hasRequiredFields = profile.country && profile.gender;
    if (hasRequiredFields) return false;
    
    // Only show for NEW accounts (created in the last 5 minutes)
    const profileCreatedAt = new Date(profile.created_at).getTime();
    const now = Date.now();
    const isNewAccount = now - profileCreatedAt < NEW_ACCOUNT_WINDOW_MS;
    
    return isNewAccount;
  }, [authLoading, profileLoading, user, profile]);

  const completeOnboarding = useCallback(async (data: { 
    displayName: string;
    handle: string;
    country: string; 
    currency: string; 
    gender: string;
    ageRange?: string;
    clothingSize?: string;
  }) => {
    await updateProfile({
      display_name: data.displayName,
      handle: data.handle,
      country: data.country,
      currency: data.currency,
      gender: data.gender,
      ...(data.ageRange && { age_range: data.ageRange }),
      ...(data.clothingSize && { clothing_size: data.clothingSize }),
    });
  }, [updateProfile]);

  return {
    needsOnboarding,
    isLoading: authLoading || profileLoading,
    completeOnboarding,
  };
}
